// src/components/SourceManagerPage.jsx
// Standalone admin page for managing the Blog Source Registry.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Panel from './common/Panel';
import CardListSkeleton from '../Skeletons/CardListSkeleton';
import TableSkeleton from '../Skeletons/TableSkeleton';
import MobileRecordCard from './common/MobileRecordCard';
import Pill from './common/Pill';
import UnderlineTabs from './common/UnderlineTabs';
import ListToolbar from './common/ListToolbar';
import EmptyState from './common/EmptyState';
import RefreshButton from './common/RefreshButton';
import RowActionsMenu from './common/RowActionsMenu';
import TableFooterSimple from './common/TableFooterSimple';
import TableViewport from './common/TableViewport';
import useListQuery from './common/listQuery/useListQuery';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import KpiRow from './common/KpiRow';
import StatTile from './common/StatTile';
import { SHADOWS, FS, SP, BUTTON_RADIUS, TOUCH_TARGET_COARSE, hoverOnly } from '../theme/tokens';
import { v2Tone } from '../theme/theme';
import PageContainer from './common/PageContainer';
import DisabledReason from './common/DisabledReason';
import ViewHeader from './ViewHeader';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ScannerIcon from '@mui/icons-material/ManageSearch';
import CloseIcon from '@mui/icons-material/Close';
import BottomSheetSelect from './common/BottomSheetSelect';
import { ConfirmDialog } from './common/confirm';
import { formatDateTime, formatNumber } from '../utils/format';
import { sourceTypeLabel } from '../utils/labels';

const API = process.env.REACT_APP_API_BASE_URL;

const SOURCE_TYPES = [
  'forum',
  'review_site',
  'manufacturer',
  'research_paper',
  'youtube_channel',
  'newsletter',
  'podcast_transcript',
];

const PRIORITY_LABELS = { 1: 'Priority 1 — Always', 2: 'Priority 2 — When relevant', 3: 'Priority 3 — Fallback' };

// 🔴 Die Hauszahl der Seitengroesse (N9.10). Die Route antwortet UNGEKAPPT
// (`list_sources` macht `.all()`), geblaettert wird deshalb CLIENTSEITIG — der
// P20-Stub setzt 50 voraus (§4.2, §5.3).
const SEITENGROESSE = 50;
const PRIORITY_GROUPS = [1, 2, 3];

// Die URL-Achsen tragen ihre BESCHRIFTUNG, nicht den Rohwert (W6, §2.2 Punkt 10):
// `useListQuery` loest den Chip-Text ueber `etikett(d, wert)` aus `d.values` auf —
// eine blosse Zeichenkette IST dort Wert UND Etikett, und Z3 laese „Status: true".
const STATUS_OPTIONEN = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];
const PRIORITY_OPTIONEN = PRIORITY_GROUPS.map((p) => ({ value: String(p), label: `Priority ${p}` }));

/**
 * Die Statuswerte des Themen-Reiters (Plan §2.5/§3.1).
 *
 * 🔴 `all` steht NICHT in dieser Liste — es ist der MARKER „ohne
 * Einschraenkung" (`useListQuery` liest ihn dann als `null`), kein Wert. Mit
 * `all` als Wert hiesse der Chip „Status: all" und sein ✕ waere ein No-Op.
 */
const THEMEN_STATUS_OPTIONEN = [
  { value: 'discovered', label: 'Discovered' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'published', label: 'Published' },
  { value: 'generating', label: 'Generating' },
  { value: 'discarded', label: 'Discarded' },
];

/**
 * Die Spaltenbreiten der Themen-Tabelle (`TableViewport.columns`, 1-basiert).
 * 🔴 Erklaerte Breiten statt gerechneter: im Leerzustand fehlen die Datenzeilen,
 * der Browser zoege die Spalten auf die Kopfbreite zusammen, und die Kopfzelle
 * spraenge zwischen Grund- und Leerzustand (P33). Summe 100 %.
 */
// 🔴 G11c-Nachbesserung 2 (2026-10-31), R16-W3: die Spalte „Status" ist NEU.
// Ohne sie sah der Nutzer bei Statusfilter „Approved"/"Discarded" lauter
// Zeilen ohne Status, von denen manche einen Approve-Knopf haben und manche
// nicht — und der einzige Ort, an dem der Grund fuer einen gesperrten
// „Discard" stand, war die aufgeklappte Zeile. Die Telefonkarte trug die
// Pille laengst; jetzt sagen Zeile und Karte dasselbe.
const THEMEN_SPALTEN = [
  { width: '8%' },   // Score
  { width: '38%' },  // Title
  { width: '18%' },  // Category
  { width: '14%' },  // Status
  { width: '12%' },  // Age
  { width: '10%' },  // Aktionen
];

/** Das Alter eines Themas in Tagen, in Worten (Hausform der Liste). */
function formatTopicAge(createdAt) {
  if (!createdAt) return '';
  const days = Math.floor((Date.now() - new Date(createdAt)) / 86400000);
  if (days === 0) return 'Today';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

/** Das Etikett eines Status — EINE Quelle (`THEMEN_STATUS_OPTIONEN`). */
function statusLabel(status) {
  const eintrag = THEMEN_STATUS_OPTIONEN.find((o) => o.value === status);
  return eintrag ? eintrag.label : (status || '—');
}

// Migrated to sonner — showSnack routes to toast.* with the same call shape.
// Modulweit, weil Schale UND Rumpf ihn brauchen.
function showSnack(msg, severity = 'success') {
  const fn = severity === 'success' ? toast.success
    : severity === 'error' ? toast.error
    : severity === 'warning' ? toast.warning
    : toast.info
  fn(msg)
}

const EMPTY_FORM = {
  name: '',
  url: '',
  type: 'forum',
  domains: '',
  canonical_types_relevant: '',
  language: 'en',
  priority: 1,
  scan_enabled: false,
  scan_paths: '',
  notes: '',
  min_nerd_score: 3,
  is_competitor: false,
  is_neutral_default: false,
  active: true,
};

function sourceToForm(s) {
  return {
    name: s.name || '',
    url: s.url || '',
    type: s.type || 'forum',
    domains: (s.domains || []).join(', '),
    canonical_types_relevant: (s.canonical_types_relevant || []).join(', '),
    language: s.language || 'en',
    priority: s.priority ?? 1,
    scan_enabled: !!s.scan_enabled,
    scan_paths: (s.scan_paths || []).join('\n'),
    notes: s.notes || '',
    min_nerd_score: s.min_nerd_score ?? 3,
    is_competitor: !!s.is_competitor,
    is_neutral_default: !!s.is_neutral_default,
    active: s.active !== false,
  };
}

function formToPayload(f) {
  return {
    name: f.name.trim(),
    url: f.url.trim(),
    type: f.type,
    domains: f.domains.split(',').map(d => d.trim()).filter(Boolean),
    canonical_types_relevant: f.canonical_types_relevant.split(',').map(d => d.trim()).filter(Boolean),
    language: f.language.trim() || 'en',
    priority: Number(f.priority),
    scan_enabled: f.scan_enabled,
    scan_paths: f.scan_paths.split('\n').map(p => p.trim()).filter(Boolean),
    notes: f.notes.trim() || null,
    min_nerd_score: Number(f.min_nerd_score),
    is_competitor: f.is_competitor,
    is_neutral_default: f.is_neutral_default,
    active: f.active,
  };
}

// ─── Source Card ─────────────────────────────────────────────────────────────
function SourceCard({ source, onEdit, onDelete, onScan, scanning }) {
  // 🔴 K-6 (UI2 Teil 8, 2026-10-10, Regel 22 §3 „Unknown is never 0"): der
  // Fehlschlag setzte die Zahl auf 0 und behauptete damit eine Kenntnis, die
  // nicht da ist. `undefined` ist jetzt der Ladezustand, `null` heisst
  // „unbekannt" und zeigt einen Strich.
  const [topicCount, setTopicCount] = useState(undefined);

  useEffect(() => {
    axios.get(`${API}/api/admin/blog/sources/${source.id}/topics`)
      .then(r => setTopicCount(r.data.length))
      .catch(() => setTopicCount(null));
  }, [source.id]);

  const topicCountLabel = topicCount === undefined ? '…' : topicCount === null ? '—' : topicCount;
  const lastScanned = formatDateTime(source.last_scanned_at, { placeholder: 'never' });

  // 🔴 G11b, Plan §2.4 / Q1 (Weg A): die drei Symbole werden EINE waagerechte
  // Zeile — Hauptknopf „Edit" + ⋮. Vorher war die Aktionsspalte ein
  // `Stack direction="column"`, und das Scan-Symbol existierte nur bei
  // `scan_enabled` (`:238`): der Deactivate-Knopf sass je Karte auf anderer
  // Hoehe („Löschen springt"). Der ⋮ traegt den ZEILENBEZUG im Namen (P19).
  // 🔴 „Scan now" ist ohne `scan_enabled` NICHT versteckt, sondern `disabled`
  // MIT dem Grund im sichtbaren Text — eine Information nur im Tooltip
  // erreichen Touch und Tastatur nicht (Hauslehre aus G13a).
  const aktionen = [
    {
      key: 'scan',
      label: source.scan_enabled
        ? (scanning ? 'Scanning…' : 'Scan now')
        : 'Scan now — scanning is off for this source',
      icon: <PlayArrowIcon fontSize="small" />,
      disabled: !source.scan_enabled || scanning,
      onClick: () => onScan(source),
    },
    {
      key: 'toggle',
      label: source.active ? 'Deactivate' : 'Reactivate',
      icon: <DeleteIcon fontSize="small" />,
      danger: source.active,
      divider: true,
      onClick: () => onDelete(source),
    },
  ];

  return (
    // 🔴 Die drei Kartenmarken (§2.4, B6/W3/NB1). Die Quellenkarte ist auf
    // BEIDEN Breiten dieselbe `SourceCard`; sie wird NICHT durch
    // `MobileRecordCard` ersetzt. Ohne `karte-titel-` waere P19 auf Sources
    // strukturell rot (`ohneZeilenbezug`), obwohl die Karte in Ordnung ist.
    <Box
      data-testid={`karte-kennung-${source.id}`}
      sx={{
        border: '1px solid',
        borderColor: source.active ? 'divider' : 'error.light',
        borderRadius: 2,
        p: 2,
        opacity: source.active ? 1 : 0.55,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box flex={1} minWidth={0}>
          <Stack direction="row" alignItems="center" spacing={1} mb={0.5} flexWrap="wrap">
            <Typography
              variant="subtitle1"
              fontWeight={600}
              noWrap
              data-testid={`karte-titel-${source.id}`}
            >
              {source.name}
            </Typography>
            {/* 🔴 Der Status traegt Text fuer BEIDE Werte (§2.4). Bis G11b
                rendert die Karte nur `inactive` — ein aktiver Zustand hatte
                GAR KEINEN Text, `messeP22` zaehlte dann 0 Orte und war gruen,
                ohne etwas geprueft zu haben. */}
            <Chip
              data-testid={`karte-status-${source.id}`}
              label={source.active ? 'active' : 'inactive'}
              size="small"
              color={source.active ? 'success' : 'error'}
              variant={source.active ? 'outlined' : 'filled'}
            />
            <Chip label={sourceTypeLabel(source.type)} size="small" variant="outlined" />
            <Chip label={source.language.toUpperCase()} size="small" variant="outlined" />
            {source.scan_enabled && (
              // 🔴 K-3/K-6 (UI2 Teil 8, 2026-10-10): die MUI-Farbangabe `info`
              // schrieb `palette.info.main` als TEXTfarbe auf Papier — unter den
              // 4,5:1 fuer normalen Text. Der Hauswert `v2Tone(theme,'info').main`
              // traegt sie und bleibt blau (Rahmen mitgezogen).
              <Chip
                label="scan on"
                size="small"
                variant="outlined"
                sx={(th) => ({
                  color: v2Tone(th, 'info').main,
                  borderColor: v2Tone(th, 'info').main,
                })}
              />
            )}
            {source.is_neutral_default && (
              <Chip label="neutral default" size="small" color="success" variant="outlined" />
            )}
            {source.is_competitor && (
              <Chip label="competitor" size="small" color="error" />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.5} mb={0.75}>
            <LinkIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {source.url}
            </Typography>
          </Stack>

          {source.domains?.length > 0 && (
            <Stack direction="row" flexWrap="wrap" spacing={0.5} mb={0.75}>
              {source.domains.map(d => (
                <Chip key={d} label={d} size="small" sx={{ fontSize: 11 }} />
              ))}
            </Stack>
          )}

          {source.notes && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {source.notes.length > 180 ? source.notes.slice(0, 180) + '…' : source.notes}
            </Typography>
          )}

          <Stack direction="row" spacing={2} mt={0.5}>
            <Typography variant="caption" color="text.secondary">
              Topics discovered: {topicCountLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Min nerd score: {source.min_nerd_score}/5
            </Typography>
            {source.scan_enabled && (
              <Typography variant="caption" color="text.secondary">
                Last scan: {lastScanned}
              </Typography>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.5} flexShrink={0} alignItems="center">
          {/* 🔴 R16-W4: der zugängliche Name nennt die Zeile — elf gleichnamige
              „Edit"-Knoepfe sind fuer einen Screenreader nicht unterscheidbar
              (WCAG 2.4.6). Der SICHTBARE Text bleibt „Edit", genau wie beim ⋮
              daneben („Actions for <Name>", :308). */}
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            aria-label={`Edit ${source.name}`}
            onClick={() => onEdit(source)}
          >
            Edit
          </Button>
          <RowActionsMenu actions={aktionen} label={`Actions for ${source.name}`} />
        </Stack>
      </Stack>
    </Box>
  );
}

// ─── Edit / Create Dialog ─────────────────────────────────────────────────────
function SourceDialog({ open, source, onClose, onSaved }) {
  const isNew = !source;
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(source ? sourceToForm(source) : EMPTY_FORM);
      setError('');
    }
  }, [open, source]);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));
  const setCheck = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.checked }));
  const setDirect = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleSave = async () => {
    const payload = formToPayload(form);
    if (!payload.name || !payload.url || !payload.type) {
      setError('Name, URL and type are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let result;
      if (isNew) {
        result = await axios.post(`${API}/api/admin/blog/sources`, payload);
      } else {
        result = await axios.patch(`${API}/api/admin/blog/sources/${source.id}`, payload);
      }
      onSaved(result.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isNew ? 'Add Source' : `Edit: ${source?.name}`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} pt={1}>
          {error && <Alert severity="error">{error}</Alert>}

          <Stack direction="row" spacing={2}>
            <TextField label="Name" value={form.name} onChange={set('name')} fullWidth required />
            <TextField label="URL" value={form.url} onChange={set('url')} fullWidth required />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select value={form.type} label="Type" onChange={set('type')}>
                {SOURCE_TYPES.map(t => <MenuItem key={t} value={t}>{sourceTypeLabel(t)}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField label="Language (e.g. en, de)" value={form.language} onChange={set('language')} sx={{ width: 160 }} />
            <FormControl sx={{ width: 220 }}>
              <InputLabel>Priority</InputLabel>
              <Select value={form.priority} label="Priority" onChange={e => setDirect('priority', Number(e.target.value))}>
                <MenuItem value={1}>1 — Always include</MenuItem>
                <MenuItem value={2}>2 — When relevant</MenuItem>
                <MenuItem value={3}>3 — Fallback only</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <TextField
            label="Domains (comma-separated, e.g. espresso_technique, grinder_reviews)"
            value={form.domains}
            onChange={set('domains')}
            fullWidth
            helperText="Topical tags used to match sources to blog topics"
          />

          <TextField
            label="Relevant canonical types (comma-separated, leave empty = all)"
            value={form.canonical_types_relevant}
            onChange={set('canonical_types_relevant')}
            fullWidth
            helperText="e.g. single_dose_espresso_grinder, burr_set — empty means relevant for all products"
          />

          <TextField
            label="Scan paths (one per line)"
            value={form.scan_paths}
            onChange={set('scan_paths')}
            fullWidth
            multiline
            minRows={2}
            helperText="Sub-paths to scope the proactive scanner, e.g. /espresso/ or /grinders/"
          />

          <Box>
            <Typography variant="body2" gutterBottom>
              Min nerd score: <strong>{form.min_nerd_score}/5</strong>
              {' '}— only topics scoring this or higher are saved from this source
            </Typography>
            <Slider
              value={form.min_nerd_score}
              onChange={(_, v) => setDirect('min_nerd_score', v)}
              min={1} max={5} step={1}
              marks={[1,2,3,4,5].map(v => ({ value: v, label: v }))}
              sx={{ maxWidth: 320, ml: 1 }}
            />
          </Box>

          <TextField
            label="Notes (injected verbatim into AI prompts — write like briefing a researcher)"
            value={form.notes}
            onChange={set('notes')}
            fullWidth
            multiline
            minRows={4}
            helperText='e.g. "Prioritize threads with 50+ replies. Avoid buying-recommendation posts. Focus on technique and comparative analysis."'
          />

          <Divider />
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              control={<Switch checked={form.scan_enabled} onChange={setCheck('scan_enabled')} />}
              label="Scan enabled (proactive scanner)"
            />
            <FormControlLabel
              control={<Switch checked={form.active} onChange={setCheck('active')} />}
              label="Active (included in prompt injection)"
            />
          </Stack>
          <Stack direction="row" spacing={3} flexWrap="wrap">
            <FormControlLabel
              control={<Switch checked={form.is_neutral_default} onChange={setCheck('is_neutral_default')} color="success" />}
              label={
                <Box>
                  <Typography variant="body2">Neutral default</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Always included — even in transcript-primary mode
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              control={<Switch checked={form.is_competitor} onChange={setCheck('is_competitor')} color="error" />}
              label={
                <Box>
                  <Typography variant="body2">Competitor source</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Never injected into generation prompts
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : isNew ? 'Add source' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Topics Inbox ─────────────────────────────────────────────────────────────

const ScoreBreakdownChips = ({ topic }) => {
  const labels = topic.engagement_signals?.score_breakdown?.labels;
  if (!labels?.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
      {labels.map((lbl, i) => (
        <Chip
          key={i}
          label={lbl}
          size="small"
          sx={{ height: 18, fontSize: FS.xxs, bgcolor: 'background.default', border: '1px solid', borderColor: 'divider' }}
        />
      ))}
    </Box>
  );
};

/**
 * ThemenDetail — der aufgeklappte Teil einer Themenzeile (Plan §2.5, N11.3).
 *
 * Dieselben Felder in BEIDEN Formen: die Desktop-Zeile klappt sie unter sich
 * auf, die mobile Karte reicht sie als `expanded` an `MobileRecordCard`. Ein
 * zweiter Aufbau waere die Doppelquelle, gegen die die Zonen angetreten sind.
 */
function ThemenDetail({ topic: t }) {
  const ns = t.nerd_signals || {};
  const hatNichts = !t.why_educational && !t.abstract && !t.editorial_brief
    && !(t.source_urls?.length > 0) && Object.keys(ns).length === 0 && !t.operator_notes;
  if (hatNichts) return null;
  return (
    <Box sx={{ bgcolor: 'action.hover', p: 2 }}>
      {(t.why_educational || t.abstract) && (
        <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic', color: 'text.secondary', lineHeight: 1.55 }}>
          {t.why_educational || t.abstract}
        </Typography>
      )}
      {t.editorial_brief && (
        <Box sx={{ mt: 1, p: 1.5, borderRadius: `${BUTTON_RADIUS}px`, bgcolor: 'action.selected', border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Editorial Brief
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: FS.base, color: 'text.primary' }}>
            {t.editorial_brief}
          </Typography>
        </Box>
      )}
      {Object.keys(ns).length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Nerd Signals
          </Typography>
          <Stack spacing={0.5}>
            {[
              { key: 'technical_specificity', label: 'Technical specificity', max: 2 },
              { key: 'audience_level', label: 'Audience level', max: 2 },
              { key: 'actionable_depth', label: 'Actionable depth', max: 1 },
              { key: 'community_signal', label: 'Community signal', max: 1 },
            ].map(({ key, label, max }) => {
              const val = ns[key] ?? null;
              if (val === null) return null;
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: SP.tight }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs, flex: '0 0 140px' }}>
                    {label}
                  </Typography>
                  <Stack direction="row" spacing={0.25}>
                    {Array.from({ length: max }).map((_, i) => (
                      <Box key={i} sx={{
                        width: 10, height: 10, borderRadius: 0.5,
                        bgcolor: i < val ? 'text.secondary' : 'divider',
                      }} />
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: FS.xs }}>
                    {val}/{max}
                  </Typography>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}
      <ScoreBreakdownChips topic={t} />
      {t.source_urls?.length > 0 && (
        <Box sx={{ mt: 1.25 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Sources
          </Typography>
          <Stack spacing={0.25}>
            {t.source_urls.map((url, i) => (
              <Typography key={i} variant="caption" sx={{ fontSize: FS.xs }}>
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', opacity: 0.65 }}>
                  {url.replace(/^https?:\/\//, '').slice(0, 80)}{url.length > 85 ? '…' : ''}
                </a>
              </Typography>
            ))}
          </Stack>
        </Box>
      )}
      {t.operator_notes && (
        <Box sx={{ mt: 1.25, p: 1, borderRadius: `${BUTTON_RADIUS}px`, border: '1px dashed', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs }}>
            {t.operator_notes}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * ThemenKarte — die mobile Form einer Themenzeile (Plan §2.5, N11.3/P15).
 *
 * 🔴 Titel ist der Weg in den Datensatz (`expanded`), Status/Score/Kategorie in
 * `chips`. 🔴 **G11c-Nachbesserung 2 (2026-10-31), R16-W4: KEIN `id`-Slot
 * mehr.** Er zeigte die rohe UUID als eigene Zeile unter dem Titel;
 * `MobileRecordCard` meint mit `id` eine MENSCHLICHE Kennung („RMA-2099-0001").
 * Themen haben keine, und die Marke `karte-kennung-` sitzt seit NB1 am
 * Kartenkasten (P19/P20 zaehlen dort).
 * 🔴 `fieldTestIds` ist Pflicht, nicht Kosmetik: ohne `chips` zaehlt P22 0
 * Statusorte, ohne `title` kann P19 den Zeilenbezug des ⋮ nicht pruefen.
 *
 * 🔴 **G11c-Nachbesserung 1 (2026-10-30): `karte-kennung-` sitzt am KARTENKASTEN,
 * nicht am `id`-Slot.** P19 zaehlt je Karte die Knoepfe INNERHALB der Marke
 * (`helfer.ts`, `messeP19`: `karte.querySelectorAll('button')`). Stand die Marke
 * am `id`-Slot — einem `Typography` mit der Kennung und ohne einen einzigen
 * Knopf —, fand die Probe dort **keinen** ⋮ und meldete „Keine Karte traegt
 * einen ⋮" fuer eine Karte, die ihn sichtbar traegt (gemessen im
 * `listen`-Lauf `g11ca`, Foto `390_themen-grund`). Die Hausform ist die
 * Quellenkarte derselben Datei (`SourceCard`): die Marke steht am Kasten.
 * P20 zaehlt die Marke weiterhin genau einmal je Karte.
 */
export function ThemenKarte({ topic: t, quelle, expanded, onToggle, onApprove, onDiscard, saving, discardDisabled, discardReason }) {
  const ns = t.nerd_signals || {};
  const hatDetail = Boolean(t.why_educational || t.abstract || t.editorial_brief
    || (t.source_urls?.length > 0) || Object.keys(ns).length > 0 || t.operator_notes);
  // 🔴 G13a-Lehre 3: in `chips` steht HOECHSTENS EINE Pille — sonst bricht der
  // Titel wortweise. Score und Kategorie gehoeren in `meta`; der Status ist die
  // Aussage, die P22 je Karte verlangt, und bleibt die Pille.
  return (
    <MobileRecordCard
      data-testid={`karte-kennung-${t.id}`}
      title={(
        <Box
          component="button"
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${t.title} — details`}
          sx={{
            // 🔴 G11c-Nachbesserung 2 (2026-10-31), R16-C1: der Aufklapper der
            // Telefonkarte war unsichtbar UND unter dem 44-px-Boden. `all:
            // 'unset'` nimmt einem nackten `button` auch die Theme-Hoehe; bei
            // einzeiligem Titel war die einzige Flaeche, die den Detailteil
            // oeffnet, ~20 px hoch. Jetzt: dasselbe Glyph wie die
            // Spotlight-Karte (`▲`/`▼`, `aria-hidden`) und der Boden aus dem
            // Token (`TOUCH_TARGET_COARSE`, dieselbe Bauart wie die ✕-Knoepfe
            // der Z3-Chips).
            all: 'unset', cursor: hatDetail ? 'pointer' : 'default', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: SP.tight, width: '100%',
            ...TOUCH_TARGET_COARSE,
          }}
        >
          <span>{t.title}</span>
          {hatDetail && (
            // 🔴 G11c-Nachbesserung 4 (2026-10-31), R16-W9: das Glyph trug
            // `text.disabled` (MUI-Vorgabe rgba(0,0,0,0.38) ≈ 2,7:1 auf Weiss)
            // und lag damit unter dem 3:1-Boden fuer Bedienzustaende (WCAG 2.1
            // 1.4.11). Auf dem Telefon ist es die EINZIGE sichtbare Affordanz
            // des Aufklappers — kein Hover, kein Zeiger. `text.secondary` = 5,7:1.
            <Box component="span" aria-hidden="true" sx={{ flexShrink: 0, color: 'text.secondary', fontSize: FS.xs }}>
              {expanded ? '▲' : '▼'}
            </Box>
          )}
        </Box>
      )}
      meta={[
        `Score ${t.relevance_score ?? '—'}`,
        t.content_category ? t.content_category.replace(/_/g, ' ') : null,
        quelle,
        formatTopicAge(t.created_at),
      ].filter(Boolean).join(' · ')}
      chips={<Pill>{statusLabel(t.status)}</Pill>}
      actions={(
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
          {(t.status === 'discovered' || t.status === 'pending') && (
            <Button variant="contained" size="small" disabled={saving} onClick={onApprove}>
              Approve
            </Button>
          )}
          <RowActionsMenu
            label={`Actions for ${t.title}`}
            actions={[{
              key: 'discard',
              label: 'Discard',
              danger: true,
              disabled: discardDisabled,
              onClick: onDiscard,
            }]}
          />
        </Stack>
      )}
      expanded={expanded ? (
        <>
          <ThemenDetail topic={t} />
          {/* 🔴 G11c-Nachbesserung 2 (2026-10-31), R15-B3: der Grund des
              gesperrten „Discard" stand auf dem Telefon NIRGENDS — das ⋮-Menue
              rendert einen `MenuItem disabled` ohne Text, und die
              `DisabledReason`-Flaeche sass nur in der Desktop-Aufklappzeile.
              Hier steht er als SICHTBARE Zeile (kein Tooltip: Touch und
              Tastatur erreichen ihn nicht, G13a-Lehre 4). */}
          {discardReason && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid={`karte-discard-grund-${t.id}`}
              sx={{ display: 'block', pt: SP.tight }}
            >
              {`Discard unavailable: ${discardReason}`}
            </Typography>
          )}
        </>
      ) : null}
      fieldTestIds={{
        chips: `karte-status-${t.id}`,
        title: `karte-titel-${t.id}`,
      }}
    />
  );
}

export function TopicsInbox({ stats, onZahlGeaendert }) {
  // ── Die EIGENE Instanz (Plan §3.2) ────────────────────────────────────────
  // 🔴 `topic_status` traegt die VORGABE `discovered`, und der Anfrage-Bauer
  // sendet den EFFEKTIVEN Zustand (nicht „Parameter vorhanden?"): der Server
  // liest „kein `status`" als ALLE Themen (`blog_api.py`) — eine Vorgabe, die
  // nur in der URL fehlt, aenderte hier still die Menge.
  // 🔴 `all` ist der MARKER und steht darum NICHT in `values`: mit `all` in der
  // Liste waere `istMarker` false, der Chip hiesse „Status: all", und sein ✕
  // schriebe denselben Wert zurueck (No-Op) — §2.3, am Hook belegt.
  // 🔴 `page` ist die EINGEBAUTE Achse, keine Dimension (BL-A): als Dimension
  // wirft `pruefeKonfiguration` beim ersten Render (`RESERVIERT`).
  const dimensionen = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    {
      key: 'topic_status',
      kind: 'single',
      label: 'Status',
      default: 'discovered',
      values: THEMEN_STATUS_OPTIONEN,
    },
  ], []);
  const query = useListQuery({ dimensions: dimensionen });
  const { state, set, chips, clearAll } = query;

  const [topics, setTopics] = useState([]);
  // 🔴 K-6/§2.5: „keine Themen" und „nicht geladen" sind zwei Aussagen. Der
  // frueher leere `catch` liess nach einem Fehlschlag „No topics found." stehen —
  // eine FALSCHE Aussage ueber den Bestand.
  const [leseFehler, setLeseFehler] = useState(null);
  const [jeGeladen, setJeGeladen] = useState(false);
  // 🔴 G11c-Nachbesserung 2 (2026-10-31), R16-W6: ob schon EINMAL geladen
  // wurde, entscheidet, ob ein Fehlschlag eine Snackbar bekommt. Im ERSTEN
  // Aufbau tragen der `Alert` und der Leerzustand die Aussage bereits — eine
  // dritte Meldung fuer denselben Vorgang. Ein Ref, kein State: `reload` haengt
  // an `[state.topic_status, state.q]`, und ein zusaetzlicher Dep-Eintrag
  // liesse den Effekt bei jedem Erfolg erneut laufen.
  const jeGeladenRef = useRef(false);
  const [laedt, setLaedt] = useState(false);
  const [saving, setSaving] = useState({});
  const [cleaning, setCleaning] = useState(false);
  const [rescoring, setRescoring] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [quellen, setQuellen] = useState({});

  const thema = useTheme();
  const mobil = useMediaQuery(thema.breakpoints.down('sm'));

  const reload = useCallback(async () => {
    setLaedt(true);
    try {
      const params = {};
      if (state.topic_status) params.status = state.topic_status;
      if (state.q) params.q = state.q;
      const r = await axios.get(`${API}/api/admin/blog/topics`, { params });
      setTopics(Array.isArray(r.data) ? r.data : []);
      setLeseFehler(null);
      // 🔴 `jeGeladen` im ERFOLGSZWEIG, nie in `finally`: sonst faellt die
      // Sperre des ersten Aufbaus auch dann, wenn er gescheitert ist.
      setJeGeladen(true);
      jeGeladenRef.current = true;
    } catch (e) {
      setLeseFehler(e);
      // Die alte Liste bleibt STEHEN (R16-W-2, K-6): nach einem
      // fehlgeschlagenen NACHladen ist die Zahl der sichtbaren Zeilen bekannt,
      // nur ihre Frische nicht — und DANN ist die Snackbar der richtige Weg
      // (die Liste steht ja, nur nicht frisch).
      if (jeGeladenRef.current) showSnack('Failed to load topics', 'error');
    } finally {
      setLaedt(false);
    }
  }, [state.topic_status, state.q]);

  useEffect(() => { reload(); }, [reload]);

  // Die Quellennamen fuer die Metazeile (`Quelle · Signal`). Sie haengen an
  // keinem Filter — EIN Abruf, nicht je Tastendruck.
  useEffect(() => {
    axios.get(`${API}/api/admin/blog/sources`)
      .then((r) => setQuellen(Object.fromEntries((r.data || []).map((s) => [s.id, s.name]))))
      .catch(() => setQuellen({}));
  }, []);

  const setStatus = async (topic, status) => {
    setSaving(s => ({ ...s, [topic.id]: true }));
    try {
      await axios.patch(`${API}/api/admin/blog/topics/${topic.id}`, { status });
      setTopics(ts => ts.map(t => t.id === topic.id ? { ...t, status } : t));
      setExpandedId(prev => prev === topic.id ? null : prev);
      // Der Statuswechsel verschiebt die Zahlen der KPI-Reihe UND den Reiter-
      // Zaehler — beide haengen an `/topics/stats`, nicht an der Liste.
      onZahlGeaendert?.();
    } finally {
      setSaving(s => ({ ...s, [topic.id]: false }));
    }
  };

  const runCleanup = async () => {
    setCleaning(true);
    try {
      await axios.post(`${API}/api/admin/blog/topics/cleanup`);
      await reload();
      onZahlGeaendert?.();
    } finally {
      setCleaning(false);
    }
  };

  const runRescore = async () => {
    setRescoring(true);
    try {
      await axios.post(`${API}/api/admin/blog/topics/rescore-all`);
      await reload();
      onZahlGeaendert?.();
    } finally {
      setRescoring(false);
    }
  };

  const formatEngagement = (t) => {
    const eng = t.engagement_signals || {};
    const source = quellen[t.source_id] || null;
    const replies = eng.replies ? `${eng.replies} replies` : null;
    const views   = eng.views   ? `${formatNumber(eng.views / 1000, { digits: 0 })}k views` : null;
    const upvotes = eng.upvotes ? `${eng.upvotes} upvotes` : null;
    const signal  = replies || views || upvotes || null;
    if (source && signal) return `${source} · ${signal}`;
    if (source) return source;
    if (signal) return signal;
    return null;
  };

  // Spotlight: top 5 by score (score ≥ 70), independent of status filter
  const spotlightTopics = useMemo(() => (
    [...topics]
      .filter(t => (t.relevance_score ?? 0) >= 70 && ['discovered', 'pending', 'approved'].includes(t.status))
      .sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0))
      .slice(0, 5)
  ), [topics]);

  // Table: all topics not already shown as spotlight cards, sorted by score desc
  const tableTopics = useMemo(() => {
    const spotlightIds = new Set(spotlightTopics.map(t => t.id));
    return [...topics]
      .filter(t => !spotlightIds.has(t.id))
      .sort((a, b) => (b.relevance_score ?? 0) - (a.relevance_score ?? 0));
  }, [topics, spotlightTopics]);

  // ── Blaettern, Zaehler, Leerzustand (§2.5, §4.2) ─────────────────────────
  // Die Route antwortet UNGEKAPPT (`blog_api.py`), `n` ist damit die exakte
  // Trefferzahl und die Seite schneidet clientseitig. 🔴 Geschnitten wird
  // `tableTopics`, NICHT `topics`: die Spotlight-Karten stehen ausserhalb der
  // Tabelle, und ein Schnitt ueber `topics` haette die Seitengroesse um bis zu
  // fuenf Zeilen unterschritten (P20 zaehlt die `tbody tr`).
  const seite = state.page || 1;
  const sichtbar = tableTopics.slice((seite - 1) * SEITENGROESSE, seite * SEITENGROESSE);

  // N10.4: liefert die Seite nichts mehr und gibt es Treffer, springt sie auf
  // die letzte — `replace`, kein neuer Verlaufseintrag.
  useEffect(() => {
    if (!laedt && tableTopics.length > 0 && sichtbar.length === 0 && seite > 1) {
      set('page', Math.max(1, Math.ceil(tableTopics.length / SEITENGROESSE)));
    }
  }, [laedt, tableTopics.length, sichtbar.length, seite, set]);

  // 🔴 Die Sperre gilt fuer den ERSTEN Aufbau, nicht fuer einen Fehler NACH
  // einer Aenderung (N8.5/N4.5).
  const sperre = Boolean(leseFehler) && !jeGeladen;
  const leer = !laedt && topics.length === 0 && (jeGeladen || Boolean(leseFehler));
  // 🔴 Der Ladezustand hat einen EIGENEN Zweig VOR dem Leerzustand (G13a-Lehre
  // 1): ohne ihn erschiene waehrend des ersten Abrufs eine leere Tabelle, also
  // ein Leerzustand, der „nichts da" behauptet, waehrend noch geladen wird.
  const geruest = !jeGeladen && !leseFehler;
  const hatSuche = Boolean(state.q);
  const eingeschraenkt = chips.some(c => !c.isDefault);
  const grundbestand = typeof stats?.total === 'number' ? stats.total : null;

  // 🔴 Vier Gruende, nicht drei: „keine Themen" ist nur WAHR, wenn der
  // Grundbestand 0 ist. Ist die Vorgabe `discovered` gesetzt und der Bestand
  // > 0, ist eine leere Liste ein Nichttreffer (`no-match`) — „No topics yet"
  // waere dann eine falsche Aussage ueber den Bestand. Ist der Bestand
  // UNBEKANNT (Statistik gefallen), ist auch das eine eigene Aussage
  // (Regel 22, Komponentenregel 3: unbekannt ist nie 0).
  const grund = leseFehler
    ? 'error'
    : eingeschraenkt
      ? 'no-match'
      : grundbestand === 0
        ? 'no-data'
        : grundbestand == null
          ? 'unknown'
          : 'no-match';

  const zaehler = {
    n: jeGeladen ? topics.length : null,
    m: grundbestand,
    noun: 'topics',
    loading: laedt && !jeGeladen,
  };

  // 🔴 Zwei Wege, EIN Knopf. MIT Grund traegt der Baustein den Tooltip (der
  // Knopf ist gesperrt und damit nicht fokussierbar — der Halter macht den
  // Grund tastaturerreichbar). OHNE Grund ist der Knopf AKTIV und bekommt den
  // sichtbaren Hinweis zurueck; der Baustein allein liesse ihn ohne jede
  // Sprechblase.
  //
  // 🔴 Und HIER bleibt ein dritter, leerer Weg fuer den LADEZUSTAND: waehrend
  // `saving` ist der Knopf gesperrt und gibt es keinen Grund — dann kommt er
  // NACKT. Anders als in `HomeDashboard` verliert diese Stelle dabei nichts:
  // vor K-N hing der Tooltip DIREKT um den gesperrten `IconButton` und ging
  // dort nie auf (ein gesperrtes Kind feuert keine Ereignisse) — der gesperrte
  // Zustand trug hier also noch nie einen sichtbaren Hinweis. Ein `Tooltip` um
  // ein gesperrtes `button`-Kind kann nie aufgehen; MUI warnt dafuer
  // ausdruecklich („A disabled element does not fire events").
  const discardDisabled = (t) => saving[t.id] || ['discarded', 'published', 'generating'].includes(t.status);
  // 🔴 G11c-Nachbesserung 4 (2026-10-31), R16-W10: der Satz nannte das falsche
  // Objekt („This source") — er steht in einer Liste von THEMEN. Der Wortlaut
  // ist vorbestehend, aber diese Runde macht ihn zur sichtbaren Zeile und der
  // Changelog verspricht ihn; ein Text, der die Flaeche nicht benennt, ist
  // falsch, sobald man ihn sieht.
  const discardReason = (t) => saving[t.id]
    ? null
    : ['discarded', 'published', 'generating'].includes(t.status)
      ? 'This topic is already discarded or published, or is still being generated.'
      : null;
  const discardButton = (t) => (
    <IconButton
      size="small"
      color="error"
      aria-label="Discard"
      disabled={discardDisabled(t)}
      onClick={() => setStatus(t, 'discarded')}
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  );
  const discardControl = (t) => discardReason(t)
    ? <DisabledReason reason={discardReason(t)}>{discardButton(t)}</DisabledReason>
    : discardDisabled(t)
      ? discardButton(t)
      : <Tooltip title="Discard" describeChild>{discardButton(t)}</Tooltip>;

  // ── Die Leiste (§2.5) ────────────────────────────────────────────────────
  // Eine inline Facette „Status" am Desktop — mobil steht DASSELBE Kriterium
  // im Blatt (N11.8: Facetten sind unter `sm` nie inline).
  const facetten = [
    <BottomSheetSelect
      key="status"
      label="Status"
      desktopForm="pill"
      value={state.topic_status || ''}
      onChange={(v) => set('topic_status', v || null)}
      options={THEMEN_STATUS_OPTIONEN}
      disabled={sperre}
      clearable
    />,
  ];
  const kriterien = [
    {
      key: 'topic_status',
      kind: 'select',
      label: 'Status',
      value: state.topic_status || '',
      onChange: (v) => set('topic_status', v || null),
      options: THEMEN_STATUS_OPTIONEN,
      // 🔴 Ausdruecklich `false` (P25): `FilterSheet` rendert bei `clearable`
      // eine „All"-Zeile, und die ist im Blatt verboten — der Marker ist der
      // leere Wert, nicht eine Option.
      clearable: false,
      emptyLabel: 'Any',
    },
  ];
  // 🔴 Seiten-⋮, nicht Zeilen-⋮ (§2.5): „Remove stale topics" und „Rescore all"
  // wirken auf den GANZEN Bestand — an einer Zeile waeren sie eine Luege.
  const seitenAktionen = [
    {
      key: 'cleanup',
      label: cleaning ? 'Removing stale topics…' : 'Remove stale topics',
      danger: true,
      disabled: cleaning,
      onClick: runCleanup,
    },
    {
      key: 'rescore',
      label: rescoring ? 'Rescoring…' : 'Rescore all',
      disabled: rescoring,
      onClick: runRescore,
    },
  ];

  // Der EINE Leerzustand der Seite (P9: genau ein `[data-reason]`) — die Liste
  // rendert ihren eigenen nicht.
  const leerText = grund === 'no-data'
    ? 'No topics yet. Run a scan to discover new topics.'
    : grund === 'error'
      ? 'Could not load topics.'
      : grund === 'unknown'
        ? 'The total is unknown — this may be an empty list.'
        : (grund === 'no-match' && !hatSuche)
          ? 'No topics match these filters.'
          : undefined;
  const leerzustand = leer ? (
    <EmptyState
      reason={grund}
      message={leerText}
      showDuringOutage={grund === 'error'}
      noun="topic"
      query={grund === 'no-match' && hatSuche ? state.q : undefined}
      searchedIn={grund === 'no-match' && hatSuche ? 'title, keyword and abstract' : undefined}
      onClearSearch={grund === 'no-match' && hatSuche ? () => set('q', null) : undefined}
      onClearFilters={grund === 'no-match' && !hatSuche ? () => clearAll({ keepTab: true }) : undefined}
      // 🔴 G11c-Nachbesserung 2 (2026-10-31), R15-B2: „Try again" muss BEIDE
      // Staende holen. `grund === 'unknown'` heisst „`stats.total` ist
      // unbekannt" — die Statistik kommt aus der SCHALE (`onZahlGeaendert`),
      // nicht aus `reload()`. Ein Knopf, der nur die Liste nachlaedt, laesst
      // Kacheln, Reiterzaehler und den Leerzustand auf `unknown` stehen: er ist
      // fuer seinen erklaerten Zweck wirkungslos (dieselbe Form, die G11b/NB1
      // fuer die Quellenliste geschlossen hat).
      onRetry={grund === 'error' || grund === 'unknown'
        ? () => { reload(); onZahlGeaendert?.(); }
        : undefined}
    />
  ) : null;

  return (
    <Stack spacing={2}>
      {/* KPI-Reihe — High Quality | Approved | Published.
          🔴 G11c-Nachbesserung 2 (2026-10-31): diese Reihe traegt ABSICHTLICH
          KEINE Zonenmarke `liste-kpi`. Die Marke steht im Plan (§2.5, P3)
          ZWISCHEN Kopf und Reiterleiste; diese KPI-Reihe ist aber REITERINHALT
          (Themenzahlen — auf dem Quellen-Reiter waeren sie falsch) und steht
          darum unter der Reiterleiste, im Rumpf `TopicsInbox`. Ein Marker dort
          behauptete eine Reihenfolge, die der Baum nicht hat (R15-B1; die alte
          Fassung trug ihn und der Kommentar dazu war falsch). P3 laeuft auf
          dieser Seite nicht und misst damit kopf < reiter < panel — was stimmt.
          🔴 Ladeform in der FORM der Flaeche (G13a-Lehre 1): dieselbe `KpiRow`
          mit `StatTile … loading`, kein fremdes Geruest. */}
      <Box>
        <KpiRow>
          <StatTile loading={stats == null} label="High Quality" value={stats?.high_quality ?? '—'} labelTooltip="Score ≥ 70, ready to approve" />
          <StatTile loading={stats == null} label="Approved" value={stats?.approved ?? '—'} labelTooltip="Ready to generate" />
          <StatTile loading={stats == null} label="Published" value={stats?.published ?? '—'} labelTooltip="Live on Shopify" />
        </KpiRow>
      </Box>

      <Panel pad="none" clip data-testid="liste-panel">
        <Box sx={{ px: SP.card, pt: SP.card }}>
          {sperre && (
            <Alert severity="warning" data-testid="erster-aufbau-gesperrt" sx={{ mb: SP.card }}>
              {/* 🔴 G11c-Nachbesserung 2 (2026-10-31), R16-W7: „Search and
                  filters are locked" war zu weit. Suche, Facette und
                  Filter-Blatt sind gesperrt — die CHIPS in Z3 nicht
                  (`ActiveFilterBar` kennt kein `disabled`, und ihr ✕ loest
                  einen neuen Abruf aus). Der Satz nennt jetzt die
                  BEDIENELEMENTE; die Chips sind eine Quittung, kein
                  Bedienelement. Das Hauswort „locked until the list loads"
                  bleibt, damit die geteilte Sperr-Probe weiter greift. */}
              Search and filter controls are locked until the list loads. Use Try again below.
            </Alert>
          )}
          <ListToolbar
            search={{
              value: state.q ?? '',
              onChange: () => {},
              onCommit: (v) => set('q', v || null),
              placeholder: 'Search topics…',
              label: 'Search topics',
              inputRole: 'searchbox',
              disabled: sperre,
            }}
            facets={facetten}
            moreFilters={{
              disabled: sperre,
              criteria: kriterien,
              resultCount: leer ? null : topics.length,
              activeCount: chips.filter((c) => c.id !== 'q').length,
              // 🔴 Die Huelle ist Pflicht (W13): `ActiveFilterBar` ruft
              // `onClearAll?.()` OHNE Argument — `clearAll` direkt uebergeben
              // hiesse `keepTab` immer `false`, und der Reiter spraenge auf
              // „Sources".
              onClearAll: () => clearAll({ keepTab: true }),
            }}
            count={zaehler}
            refresh={
              <RefreshButton
                ariaLabel="Refresh topics"
                loading={laedt}
                firstLoad={!jeGeladen}
                // 🔴 G11b-Lehre (NB1-C1/R15-W1): der Knopf frischt BEIDE Staende
                // — Liste UND Statistik. Die KPI-Reihe und der Reiter-Zaehler
                // lesen `/topics/stats`; ein Refresh nur der Liste liesse sie
                // auf einem anderen Stand stehen.
                onClick={() => { reload(); onZahlGeaendert?.(); }}
              />
            }
            actions={seitenAktionen}
            actionsLabel="Topic list actions"
            // 🔴 G11c-Nachbesserung 2 (2026-10-31): NUR dieser Reiter laesst die
            // Z3-Zeile auf dem Telefon umbrechen. Der breite Vorgabe-Chip
            // („Status: Discovered · default") neben Zaehler und „Clear all"
            // wurde sonst angeschnitten (Foto `390_themen-grund`). Global — wie
            // in Nachbesserung 1 — kostete der Umbruch eine Zeile auf JEDER
            // mobilen Liste und riss P2 (`/fulfillment`) und M1 (`/products`)
            // der Kette `g11cc`; deshalb ist er ein Opt-in.
            z3UmbruchMobil
            query={{ chips, clearAll: () => clearAll({ keepTab: true }) }}
          >
            <Box sx={{ px: SP.card }}>
              {/* P18: die sichtbare Ordnung. Die Route sortiert
                  `relevance_score DESC NULLS LAST, created_at DESC` — der Satz
                  ist die Aussage, kein Bedienelement. */}
              <Typography variant="caption" color="text.secondary" data-testid="sortiert-nach">
                Sorted by relevance, then newest
              </Typography>
            </Box>

            {!leer && (
              <>

          {/* ── READY TO REVIEW — spotlight cards ────────────────────────── */}
          {spotlightTopics.length > 0 && (
            <Box>
              <Typography
                variant="overline"
                sx={{ display: 'block', color: 'text.secondary', letterSpacing: '0.08em', mb: 1 }}
              >
                Ready to Review · {spotlightTopics.length} topic{spotlightTopics.length !== 1 ? 's' : ''}
              </Typography>
              {/* 🔴 W10: EINE waagerechte, scrollende Reihe — keine gestapelte
                  Wand. Fuenf gestapelte Karten waeren > 1000 px hoch, und die
                  erste Tabellenzeile laege unter dem Falz (P2). Die feste
                  Kartenbreite haelt die Reihe in jeder Breite gleich hoch. */}
              <Box sx={{ display: 'flex', gap: 1.5, overflowX: 'auto', pb: 0.5 }}>
                {spotlightTopics.map(t => {
                  const isCardExpanded = expandedCardId === t.id;
                  const ns = t.nerd_signals || {};
                  const hasDetail = t.editorial_brief || t.abstract || t.why_educational ||
                    (t.source_urls?.length > 0) || Object.keys(ns).length > 0 || t.operator_notes;
                  return (
                  <Panel
                    key={t.id}
                    pad="none"
                    clip
                    sx={{ boxShadow: SHADOWS.card, flex: '0 0 300px' }}
                  >
                    {/* Card header — clickable to expand */}
                    <Box
                      onClick={() => hasDetail && setExpandedCardId(prev => prev === t.id ? null : t.id)}
                      sx={{
                        display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2, pt: 1.5, pb: 1,
                        cursor: hasDetail ? 'pointer' : 'default',
                        ...hoverOnly(hasDetail ? { bgcolor: 'action.hover' } : {}),
                      }}
                    >
                      <Box sx={{
                        flexShrink: 0, px: 0.75, py: 0.25, borderRadius: `${BUTTON_RADIUS}px`,
                        border: '1px solid', borderColor: 'divider', bgcolor: 'background.default',
                      }}>
                        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ fontSize: FS.xs }}>
                          {t.relevance_score}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.4 }}>
                          {t.title}
                        </Typography>
                        {t.primary_keyword && (
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', fontSize: FS.xs }}>
                            {t.primary_keyword}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexShrink: 0 }}>
                        {t.content_category && (
                          <Chip
                            label={t.content_category.replace(/_/g, ' ')}
                            size="small"
                            sx={{ height: 20, fontSize: FS.xs, bgcolor: 'action.hover', color: 'text.secondary' }}
                          />
                        )}
                        {hasDetail && (
                          // 🔴 G11c-Nachbesserung 4 (2026-10-31), R16-W9: dieselbe
                          // Farbe wie am Karten-Aufklapper — `text.disabled` liegt
                          // mit ≈ 2,7:1 unter dem 3:1-Boden (WCAG 2.1 1.4.11).
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs, lineHeight: 1 }}>
                            {isCardExpanded ? '▲' : '▼'}
                          </Typography>
                        )}
                      </Stack>
                    </Box>

                    {/* Collapsed detail panel */}
                    <Collapse in={isCardExpanded} unmountOnExit>
                      <Box sx={{ px: 2, pb: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>

                        {/* Why educational — 1-sentence hook */}
                        {t.why_educational && (
                          <Typography variant="body2" sx={{ pt: 1.5, pb: 1, color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.6, fontSize: FS.base }}>
                            {t.why_educational}
                          </Typography>
                        )}

                        {/* Abstract */}
                        {t.abstract && (
                          <Typography variant="body2" sx={{ pt: t.why_educational ? 0 : 1.5, pb: 1, lineHeight: 1.6, fontSize: FS.base }}>
                            {t.abstract}
                          </Typography>
                        )}

                        {/* Editorial brief */}
                        {t.editorial_brief && (
                          <Box sx={{ mt: 1, p: 1.5, borderRadius: `${BUTTON_RADIUS}px`, bgcolor: 'action.selected', border: '1px solid', borderColor: 'divider' }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              Editorial Brief
                            </Typography>
                            <Typography variant="body2" sx={{ lineHeight: 1.6, fontSize: FS.base, color: 'text.primary' }}>
                              {t.editorial_brief}
                            </Typography>
                          </Box>
                        )}

                        {/* Nerd signals breakdown */}
                        {Object.keys(ns).length > 0 && (
                          <Box sx={{ mt: 1.5 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.75, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              Nerd Signals
                            </Typography>
                            <Stack spacing={0.5}>
                              {[
                                { key: 'technical_specificity', label: 'Technical specificity', max: 2 },
                                { key: 'audience_level',        label: 'Audience level',        max: 2 },
                                { key: 'actionable_depth',      label: 'Actionable depth',      max: 1 },
                                { key: 'community_signal',      label: 'Community signal',      max: 1 },
                              ].map(({ key, label, max }) => {
                                const val = ns[key] ?? null;
                                if (val === null) return null;
                                return (
                                  <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: SP.tight }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs, flex: '0 0 140px' }}>
                                      {label}
                                    </Typography>
                                    <Stack direction="row" spacing={0.25}>
                                      {Array.from({ length: max }).map((_, i) => (
                                        <Box key={i} sx={{
                                          width: 10, height: 10, borderRadius: 0.5,
                                          bgcolor: i < val ? 'text.secondary' : 'divider',
                                        }} />
                                      ))}
                                    </Stack>
                                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: FS.xs }}>
                                      {val}/{max}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Stack>
                          </Box>
                        )}

                        {/* Score breakdown chips */}
                        {t.engagement_signals?.score_breakdown?.labels?.length > 0 && (
                          <Box sx={{ mt: 1.25 }}>
                            <ScoreBreakdownChips topic={t} />
                          </Box>
                        )}

                        {/* Source URLs */}
                        {t.source_urls?.length > 0 && (
                          <Box sx={{ mt: 1.25 }}>
                            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 0.5, fontSize: FS.xs, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                              Sources
                            </Typography>
                            <Stack spacing={0.25}>
                              {t.source_urls.map((url, i) => (
                                <Typography key={i} variant="caption" sx={{ fontSize: FS.xs }}>
                                  <a href={url} target="_blank" rel="noopener noreferrer"
                                    style={{ color: 'inherit', opacity: 0.65 }}
                                    onClick={e => e.stopPropagation()}>
                                    {url.replace(/^https?:\/\//, '').slice(0, 80)}{url.length > 85 ? '…' : ''}
                                  </a>
                                </Typography>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Operator notes */}
                        {t.operator_notes && (
                          <Box sx={{ mt: 1.25, p: 1, borderRadius: `${BUTTON_RADIUS}px`, border: '1px dashed', borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs }}>
                              {t.operator_notes}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Collapse>

                    {/* Card footer */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'action.hover',
                    }}>
                      <Typography variant="caption" color="text.secondary">
                        {[formatEngagement(t), formatTopicAge(t.created_at)].filter(Boolean).join(' · ')}
                      </Typography>
                      <Stack direction="row" spacing={0.75}>
                        {(t.status === 'discovered' || t.status === 'pending') && (
                          <Button variant="contained" size="small" disabled={saving[t.id]}
                            onClick={() => setStatus(t, 'approved')} sx={{ py: 0.5 }}>
                            Approve
                          </Button>
                        )}
                        <Button variant="outlined" color="error" size="small" disabled={saving[t.id]}
                          onClick={() => setStatus(t, 'discarded')} sx={{ py: 0.5 }}>
                          Discard
                        </Button>
                      </Stack>
                    </Box>
                  </Panel>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* 🔴 G11c-Nachbesserung 2 (2026-10-31), R16-B1: die Behauptung
              „es gibt keine" darf erst stehen, wenn der erste Abruf GELUNGEN
              ist. Vorher stand sie auch waehrend des Ladens (und nach einem
              Fehlschlag) ueber dem Geruest — zwei Aussagen ueber die Welt, die
              zu diesem Zeitpunkt unbekannt sind, direkt neben einer Flaeche,
              die „ich lade" sagt. `jeGeladen && !leseFehler` haelt den
              gueltigen Zustand „geladen, wirklich keine Kandidaten". */}
          {jeGeladen && !leseFehler
            && spotlightTopics.length === 0 && state.topic_status === 'discovered' && (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No high-quality topics yet. Next scan runs tomorrow.
            </Alert>
          )}
              </>
            )}

          {/* ── ALL TOPICS — Tabelle ab `sm`, Karten auf dem Telefon ──────── */}
          {mobil ? (
            <Box data-testid="liste-karte">
              {geruest ? (
                <CardListSkeleton cards={3} lines={3} ariaLabel="Loading topics" />
              ) : leer ? leerzustand : (
              <Stack spacing={1}>
                {sichtbar.map((t) => (
                  <ThemenKarte
                    key={t.id}
                    topic={t}
                    quelle={formatEngagement(t)}
                    expanded={expandedCardId === t.id}
                    onToggle={() => setExpandedCardId((prev) => (prev === t.id ? null : t.id))}
                    onApprove={() => setStatus(t, 'approved')}
                    onDiscard={() => setStatus(t, 'discarded')}
                    saving={Boolean(saving[t.id])}
                    discardDisabled={discardDisabled(t)}
                    discardReason={discardReason(t)}
                  />
                ))}
              </Stack>
              )}
            </Box>
          ) : (
            <Box data-testid="liste-tabelle">
              {geruest ? (
                // 🔴 G11c-Nachbesserung 4 (2026-10-31), R16-C4/R15-W1: das
                // Geruest zeichnete FUENF Spalten, die geladene Tabelle fuehrt
                // seit R16-W3 SECHS (Score · Title · Category · Status · Age ·
                // Aktionen). `TableSkeleton` verlangt „dieselbe Spaltenzahl —
                // sonst springt die Seite" (`Skeletons/TableSkeleton.jsx`), und
                // der Sprung war auf `1440_themen-laden` gegen
                // `1440_themen-grund` sichtbar. Die Zahl kommt aus der EINEN
                // Quelle der Tabelle, nicht als zweites Literal.
                //
                // 🔴 G11c-Nachbesserung 5 (2026-10-31), R16-C5: die ZAHL stimmte,
                // die GEOMETRIE nicht. `TableSkeleton` zeichnet sechs GLEICH
                // breite Zellen; die geladene Tabelle bekommt ihre sechs Breiten
                // aus `TableViewport columns` (`table-layout: fixed`), und dazu
                // dessen Fussband. Ohne die Umhuellung standen die Balken an
                // anderen Kanten als die Zellen und die Seite sprang beim
                // Eintreffen der Daten — genau der Sprung, gegen den das Geruest
                // da ist. Die Umhuellung ist derselbe Baustein, der die Breiten
                // schon kennt; `TableSkeleton` bleibt unveraendert.
                <TableViewport columns={THEMEN_SPALTEN}>
                  <TableSkeleton columns={THEMEN_SPALTEN.length} rows={5} framed={false} ariaLabel="Loading topics" />
                </TableViewport>
              ) : (
              <>
              {leerzustand}
              <TableViewport columns={THEMEN_SPALTEN}>
                <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Score</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Age</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sichtbar.map(t => (
                        <React.Fragment key={t.id}>
                          <TableRow
                            onClick={() => setExpandedId(prev => prev === t.id ? null : t.id)}
                            sx={{
                              cursor: 'pointer',
                              opacity: (t.relevance_score ?? 0) < 40 ? 0.4 : 1,
                              '&:hover td': { bgcolor: 'action.hover' },
                            }}
                          >
                            <TableCell>
                              <Typography
                                variant="caption"
                                fontWeight={700}
                                color={(t.relevance_score ?? 0) >= 70 ? 'primary.main' : 'text.secondary'}
                              >
                                {t.relevance_score ?? '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} sx={{ fontSize: FS.sm }}>
                                {t.title}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs }}>
                                {t.content_category ? t.content_category.replace(/_/g, ' ') : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Pill>{statusLabel(t.status)}</Pill>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: FS.xs }}>
                                {formatTopicAge(t.created_at)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" onClick={e => e.stopPropagation()}>
                              {/* 🔴 Genau EIN sichtbarer Knopf + ⋮ (P19). Der
                                  Zeilenbezug steht im Namen des ⋮ — ohne ihn
                                  meldet P19 „ohne Zeilenbezug". */}
                              <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                                {(t.status === 'discovered' || t.status === 'pending') && (
                                  <Button variant="contained" size="small" disabled={saving[t.id]} onClick={() => setStatus(t, 'approved')}>
                                    Approve
                                  </Button>
                                )}
                                <RowActionsMenu
                                  label={`Actions for ${t.title}`}
                                  actions={[{
                                    key: 'discard',
                                    label: 'Discard',
                                    danger: true,
                                    disabled: discardDisabled(t),
                                    onClick: () => setStatus(t, 'discarded'),
                                  }]}
                                />
                              </Stack>
                            </TableCell>
                          </TableRow>

                          {/* 🔴 Die Aufklappzeile entsteht NUR im aufgeklappten
                              Zustand. P20 zaehlt `liste-tabelle tbody tr` und
                              verlangt genau 50 — eine dauerhaft gerenderte
                              zweite Zeile je Thema haette 100 gezaehlt. */}
                          {expandedId === t.id && (
                            <TableRow sx={{ '& td': { p: 0 } }}>
                              <TableCell colSpan={6} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                                <ThemenDetail topic={t} />
                                <Stack direction="row" spacing={1} sx={{ px: 2, pb: 2 }}>
                                  {discardControl(t)}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
              </TableViewport>
              </>
              )}
            </Box>
          )}

          {/* 🔴 Die Fusszeile steht nur, wenn WIRKLICH geblaettert wird
              (`TableFooterSimple` rendert bei `count === 0` nichts und sonst
              einen Kasten mit Trennlinie — bei 7 Treffern entstuende ein
              leerer 44-px-Streifen, Hausbefund G11a R16-W2). */}
          {tableTopics.length > SEITENGROESSE && (
            <Box data-testid="liste-fuss">
              <TableFooterSimple
                count={tableTopics.length}
                page={Math.max(0, seite - 1)}
                onPageChange={(_, p) => set('page', p + 1)}
                rowsPerPage={SEITENGROESSE}
                stickyOnMobile={false}
              />
            </Box>
          )}

          {/* Fussnote: der Server raeumt beim Lesen (Bestandsverhalten, §2.5). */}
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mt: 0.5 }}>
            Topics older than 14 days with score &lt; 40 are automatically removed on page load.
          </Typography>
          </ListToolbar>
        </Box>
      </Panel>
    </Stack>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
// ─── Reiter „Sources" ────────────────────────────────────────────────────────
/**
 * SourcesBody — die Quellenliste (UI2 Teil 10, G11b, Plan §2.4).
 *
 * 🔴 Sie ist ein EIGENER Rumpf, weil sie eine EIGENE `useListQuery`-Instanz
 * haelt: die Schale besitzt nur `tab`, dieser Rumpf `q`, `source_status`,
 * `source_priority` und `page`. Eine gemeinsame Instanz ueber beide Reiter
 * erzeugte Chips aus allen Dimensionen — die Vorgabe `topic_status: discovered`
 * erschiene als „Status: Discovered" auch hier (Plan §3.2).
 *
 * 🔴 `stats` kommt von der SCHALE (`quellenZahl`, `quellenJePrioritaet`), nicht
 * von hier: die Zahl steht an der Reiterleiste UND im Zaehler UND im
 * Gruppenkopf, und zwei Abrufe koennten auseinanderlaufen.
 */
function SourcesBody({ quellenZahl, quellenJePrioritaet, onZahlGeaendert, version, onBearbeiten }) {
  // Die zwei Dimensionen liegen BEIDE im Blatt (§2.4): zwei Dimensionen sind
  // keine inline Facette, und mobil stehen Facetten nie inline (N11.8).
  const dimensionen = useMemo(() => [
    { key: 'q', kind: 'single', label: 'Search' },
    { key: 'source_status', kind: 'single', label: 'Status', values: STATUS_OPTIONEN },
    { key: 'source_priority', kind: 'single', label: 'Priority', values: PRIORITY_OPTIONEN },
  ], []);
  const query = useListQuery({ dimensions: dimensionen });
  const { state, set, chips, clearAll } = query;

  const [sources, setSources] = useState([]);
  // 🔴 K-6: „keine Quellen" und „nicht geladen" sind zwei Aussagen (Regel 22 §4).
  const [leseFehler, setLeseFehler] = useState(null);
  const [jeGeladen, setJeGeladen] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [scanningSourceId, setScanningSourceId] = useState(null);
  const [pendingToggle, setPendingToggle] = useState(null);
  const [toggleBusy, setToggleBusy] = useState(false);

  const reload = useCallback(async () => {
    setLaedt(true);
    try {
      const params = {};
      if (state.source_status) params.active = state.source_status;
      if (state.source_priority) params.priority = state.source_priority;
      if (state.q) params.q = state.q;
      const r = await axios.get(`${API}/api/admin/blog/sources`, { params });
      setSources(Array.isArray(r.data) ? r.data : []);
      setLeseFehler(null);
      // 🔴 `jeGeladen` im ERFOLGSZWEIG, nie in `finally` (§2.2 Punkt 7): sonst
      // faellt die Sperre des ersten Aufbaus auch dann, wenn er gescheitert ist.
      setJeGeladen(true);
    } catch (e) {
      setLeseFehler(e);
      // 🔴 Die alte Liste bleibt STEHEN (R16-W-2, K-6): nach einem
      // fehlgeschlagenen NACHladen ist die Zahl der sichtbaren Karten bekannt,
      // nur ihre Frische nicht. Die Meldung traegt der Toast; der Leerzustand
      // gilt nur, wenn wirklich nichts dasteht.
      showSnack('Failed to load sources', 'error');
    } finally {
      setLaedt(false);
    }
  }, [state.source_status, state.source_priority, state.q]);

  useEffect(() => { reload(); }, [reload, version]);

  const handleScan = async (source) => {
    setScanningSourceId(source.id);
    try {
      const r = await axios.post(`${API}/api/admin/blog/sources/${source.id}/scan`);
      if (r.data.triggered) {
        showSnack(`Scanning ${source.name}… new discovered topics will appear shortly.`);
      } else if (r.data.reason) {
        showSnack(`N8N_BLOG_SCANNER_WEBHOOK_URL not configured — scanner webhook not set up yet.`, 'warning');
      } else {
        showSnack(r.data.error || 'Scan trigger failed', 'error');
      }
    } catch (err) {
      showSnack(err.response?.data?.error || 'Scan trigger failed', 'error');
    } finally {
      setScanningSourceId(null);
    }
  };

  const handleDelete = (source) => setPendingToggle(source);

  const runToggle = async () => {
    const source = pendingToggle;
    if (!source) return;
    setToggleBusy(true);
    try {
      if (source.active) {
        await axios.delete(`${API}/api/admin/blog/sources/${source.id}`);
        showSnack(`Deactivated: ${source.name}`);
      } else {
        await axios.patch(`${API}/api/admin/blog/sources/${source.id}`, { active: true });
        showSnack(`Reactivated: ${source.name}`);
      }
      reload();
      onZahlGeaendert?.();
    } catch {
      showSnack('Action failed', 'error');
    } finally {
      setToggleBusy(false);
      setPendingToggle(null);
    }
  };

  // ── Zaehler, Leerzustand, Blaettern ──────────────────────────────────────
  const seite = state.page || 1;
  const sichtbar = sources.slice((seite - 1) * SEITENGROESSE, seite * SEITENGROESSE);

  // N10.4: liefert die Seite nichts mehr und gibt es Treffer, springt sie auf
  // die letzte — `replace`, kein neuer Verlaufseintrag.
  useEffect(() => {
    if (!laedt && sources.length > 0 && sichtbar.length === 0 && seite > 1) {
      set('page', Math.max(1, Math.ceil(sources.length / SEITENGROESSE)));
    }
  }, [laedt, sources.length, sichtbar.length, seite, set]);

  // 🔴 Die Sperre gilt fuer den ERSTEN Aufbau, nicht fuer einen Fehler NACH
  // einer Aenderung (N8.5/N4.5).
  const sperre = Boolean(leseFehler) && !jeGeladen;
  const leer = !laedt && sources.length === 0 && (jeGeladen || Boolean(leseFehler));
  const hatSuche = Boolean(state.q);
  // 🔴 „eingeschraenkt" heisst: mindestens ein nicht-Vorgabe-Chip. Der Such-Chip
  // (`q`) zaehlt MIT — er verengt die Liste genauso. Bis G11b hiess das hier
  // `hatEchtenFilter`; der Name las sich, als sei die Suche ausgenommen (die
  // Leiste zaehlt sie getrennt, `activeCount`), und Gruppenkopf wie Leerzustand
  // brauchen genau die weitere Lesart.
  const eingeschraenkt = chips.some(c => !c.isDefault);
  const geruest = !jeGeladen && !leseFehler;

  // 🔴 R16-W2: Ist die Liste leer, waehrend der Grundbestand > 0 ist, und ist
  // NICHTS eingeschraenkt, dann widersprechen sich die zwei Abrufe (die Liste
  // antwortet ungekappt, `blog_api.py`). Weder „no data" (der Bestand ist nicht
  // 0) noch „no match" (es ist nichts eingeschraenkt) waere wahr — und ein
  // „Clear all filters" loeste nichts. Das ist `unknown` mit eigenem Satz.
  const grund = leseFehler
    ? 'error'
    : quellenZahl == null
      ? 'unknown'
      : quellenZahl > 0
        ? (eingeschraenkt ? 'no-match' : 'unknown')
        : 'no-data';

  // `n` = Treffer der geladenen Liste (die Route antwortet ungekappt), `m` =
  // Grundbestand aus `/sources/stats`. 🔴 Vor dem ersten Erfolg ist `n` NICHT 0,
  // sondern unbekannt (leere Region) — „unbekannt ist nie 0".
  const zaehler = {
    n: jeGeladen ? sources.length : null,
    m: quellenZahl,
    noun: 'sources',
    loading: laedt && !jeGeladen,
  };

  // Der Widerspruch der zwei Abrufe (R16-W2): die Liste kam leer, der Bestand
  // meldet > 0, und nichts ist eingeschraenkt. Er traegt „Try again" — ein
  // erneuter Abruf ist der einzige Weg, den die Flaeche anbieten kann.
  const widerspruch = grund === 'unknown' && quellenZahl > 0;
  const leerText = grund === 'no-data'
    ? 'No sources yet.'
    : grund === 'error'
      ? 'Could not load sources.'
      : grund === 'unknown'
        ? (quellenZahl == null
          ? (eingeschraenkt
            ? 'The total is unknown — this may be an empty list or a filter without hits.'
            : 'The total is unknown — this may be an empty list.')
          : `The total says ${quellenZahl} sources exist, but the list came back empty — try again.`)
        : (grund === 'no-match' && !hatSuche)
          ? 'No sources match these filters.'
          : undefined;

  // 🔴 R16-W2: `no-match` ohne Suche heisst seit der Nachbesserung ZWINGEND,
  // dass ein Filter gesetzt ist (`grund` oben) — „Clear all filters" loest also
  // immer etwas. Der widerspruechliche Fall (Liste leer, Bestand > 0, nichts
  // eingeschraenkt) bietet stattdessen „Try again" an.
  const leerzustand = leer ? (
    <EmptyState
      reason={grund}
      message={leerText}
      showDuringOutage={grund === 'error'}
      noun="source"
      query={grund === 'no-match' && hatSuche ? state.q : undefined}
      searchedIn={grund === 'no-match' && hatSuche ? 'name, URL and notes' : undefined}
      onClearSearch={grund === 'no-match' && hatSuche ? () => set('q', null) : undefined}
      onClearFilters={grund === 'no-match' && !hatSuche ? () => clearAll({ keepTab: true }) : undefined}
      // 🔴 R15-W1/R16-W1 (G11b-Runde 3): „Try again" fuehrt BEIDE Abrufe wie der
      // Refresh-Knopf. Ein blankes `reload` wiederholte genau den Defekt, den
      // NB1-C1 geschlossen hat — der Knopf hiesse „Try again" und frischte den
      // gescheiterten Statistik-Abruf nicht auf. Und der Zustand „Liste leer,
      // Statistik GEFALLEN" (`quellenZahl == null`) hatte bisher gar keinen
      // Knopf, obwohl seine Ursache ein gescheiterter Abruf ist.
      onRetry={
        grund === 'error' || widerspruch || (grund === 'unknown' && quellenZahl == null)
          ? () => { reload(); onZahlGeaendert?.(); }
          : undefined
      }
    />
  ) : null;

  const kriterien = [
    {
      key: 'source_status',
      kind: 'select',
      label: 'Status',
      value: state.source_status || '',
      onChange: (v) => set('source_status', v || null),
      options: STATUS_OPTIONEN,
      // 🔴 Ausdruecklich `false` (W5): `FilterSheet` rendert bei `clearable` eine
      // „All"-Zeile, und P25 meldet sie als Verstoss.
      clearable: false,
      emptyLabel: 'Any',
    },
    {
      key: 'source_priority',
      kind: 'select',
      label: 'Priority',
      value: state.source_priority || '',
      onChange: (v) => set('source_priority', v || null),
      options: PRIORITY_OPTIONEN,
      clearable: false,
      emptyLabel: 'Any',
    },
  ];

  const gruppe = (liste, schluessel) => liste.filter((s) => s.priority === schluessel);

  // ── Zahl im Gruppenkopf (R16-B1) ─────────────────────────────────────────
  // 🔴 Der Kopf ist die Ueberschrift einer Gruppe IN DIESER Liste; seine Zahl
  // ist die Groesse der Gruppe im aktuellen Ergebnis. Bis G11b stand dort
  // `g.length` — die SEITENSCHEIBE. Eine Gruppe ueber 50 Quellen zeigte auf
  // Seite 1 „· 50" und auf Seite 2 „· 10", als Zahl DERSELBEN Gruppe.
  //   · ungefiltert ist das Ergebnis der GANZE Bestand → `by_priority` aus
  //     `/sources/stats` (dafuer wurde das Feld gebaut, `blog_api.py:304`); es
  //     bleibt auch dann richtig, wenn `/sources` je gekappt wird.
  //   · eingeschraenkt ist es der GEFILTERTE Bestand → die exakte Client-Zahl
  //     ueber `sources` (die Route antwortet ungekappt). `by_priority` ist der
  //     UNGEFILTERTE Bestand (zwei COUNTs ohne WHERE) und zeigte unter einem
  //     Filter mehr, als die Gruppe enthaelt.
  //   · `stats` unbekannt: ebenfalls die Client-Zahl — sie ist bekannt, sobald
  //     die Liste geladen ist. Nie 0, nie geraten (Regel 22, Komponentenregel 3).
  const gruppenZahl = (schluessel) => {
    if (!eingeschraenkt && quellenJePrioritaet) {
      const n = quellenJePrioritaet[String(schluessel)];
      if (typeof n === 'number') return n;
    }
    return schluessel === 'other'
      ? sources.filter((s) => !PRIORITY_GROUPS.includes(s.priority)).length
      : gruppe(sources, schluessel).length;
  };

  return (
    <Panel pad="none" clip data-testid="liste-panel">
      <Box sx={{ px: SP.card, pt: SP.card }}>
        {sperre && (
          <Alert severity="warning" data-testid="erster-aufbau-gesperrt" sx={{ mb: SP.card }}>
            Search and filters are locked until the list loads. Use Try again below.
          </Alert>
        )}
        <ListToolbar
          search={{
            value: state.q ?? '',
            onChange: () => {},
            onCommit: (v) => set('q', v || null),
            placeholder: 'Search sources…',
            label: 'Search sources',
            inputRole: 'searchbox',
            disabled: sperre,
          }}
          moreFilters={{
            disabled: sperre,
            criteria: kriterien,
            resultCount: leer ? null : sources.length,
            activeCount: chips.filter((c) => c.id !== 'q').length,
            // 🔴 Die Huelle ist Pflicht (W13): `ActiveFilterBar` ruft
            // `onClearAll?.()` OHNE Argument — `clearAll` direkt uebergeben
            // hiesse `keepTab` immer `false`, und der Reiter spraenge auf
            // „Sources".
            onClearAll: () => clearAll({ keepTab: true }),
          }}
          count={zaehler}
          refresh={
            <RefreshButton
              ariaLabel="Refresh sources"
              loading={laedt}
              firstLoad={!jeGeladen}
              // 🔴 NB1-C1 (R16-Runde 2): der Knopf frischt BEIDE Staende auf.
              // Der Gruppenkopf liest im ungefilterten Fall `by_priority` aus
              // `/sources/stats` — vorher lud der Refresh nur die Liste, und
              // nach einer Aenderung aus einer anderen Sitzung standen „Kopf 8"
              // und neun Karten untereinander. `onZahlGeaendert` ist derselbe
              // Rueckruf, den `runToggle` schon benutzt.
              onClick={() => { reload(); onZahlGeaendert?.(); }}
            />
          }
          query={{ chips, clearAll: () => clearAll({ keepTab: true }) }}
        >
          <Box sx={{ px: SP.card }}>
            {/* P18: die sichtbare Ordnung. Die Route sortiert
                `priority, name` — der Satz ist die Aussage, kein Bedienelement. */}
            <Typography variant="caption" color="text.secondary" data-testid="sortiert-nach">
              Sorted by priority, then name
            </Typography>
          </Box>

          <Box data-testid="liste-karte" sx={{ px: SP.card }}>
            {geruest ? (
              // Geruest in der FORM der geladenen Flaeche: ein Stapel Karten.
              <CardListSkeleton cards={4} lines={3} form="outlined" ariaLabel="Loading sources" />
            ) : leer ? leerzustand : (
              <Stack spacing={3}>
                {PRIORITY_GROUPS.map((p) => {
                  const g = gruppe(sichtbar, p);
                  if (g.length === 0) return null;
                  return (
                    <Box key={p}>
                      {/* N11.4: die Zahl steht im GRUPPENKOPF, nicht als Chip in
                          der Karte (der Chip war eine Doppelung, T12). */}
                      <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                        {PRIORITY_LABELS[p]} · {gruppenZahl(p)}
                      </Typography>
                      <Stack spacing={1.5}>
                        {g.map((s) => (
                          <SourceCard
                            key={s.id}
                            source={s}
                            onEdit={onBearbeiten}
                            onDelete={handleDelete}
                            onScan={handleScan}
                            scanning={scanningSourceId === s.id}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
                {/* Quellen mit unerwarteter Prioritaet — Gruppe „Other" bleibt. */}
                {(() => {
                  const andere = sichtbar.filter((s) => !PRIORITY_GROUPS.includes(s.priority));
                  if (andere.length === 0) return null;
                  return (
                    <Box>
                      <Typography variant="overline" color="text.secondary" display="block" mb={1}>
                        Other · {gruppenZahl('other')}
                      </Typography>
                      <Stack spacing={1.5}>
                        {andere.map((s) => (
                          <SourceCard
                            key={s.id}
                            source={s}
                            onEdit={onBearbeiten}
                            onDelete={handleDelete}
                            onScan={handleScan}
                            scanning={scanningSourceId === s.id}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })()}
              </Stack>
            )}
          </Box>

          {/* 🔴 Der Behaelter steht nur, wenn WIRKLICH geblaettert wird:
              `TableFooterSimple` gibt bei `count === 0` `null` zurueck und
              rendert sonst einen Kasten mit Trennlinie — bei 7 Treffern entstuende
              ein leerer 44-px-Streifen (Hausbefund G11a, R16-W2). */}
          {sources.length > SEITENGROESSE && (
            <Box data-testid="liste-fuss">
              <TableFooterSimple
                count={sources.length}
                page={Math.max(0, seite - 1)}
                onPageChange={(_, p) => set('page', p + 1)}
                rowsPerPage={SEITENGROESSE}
                stickyOnMobile={false}
              />
            </Box>
          )}
        </ListToolbar>
      </Box>

      {/* Deactivate/Reactivate-Rueckfrage — R16-A W5, ersetzt `window.confirm`. */}
      <ConfirmDialog
        open={!!pendingToggle}
        title={pendingToggle?.active ? 'Deactivate source?' : 'Reactivate source?'}
        message={pendingToggle
          ? `${pendingToggle.name} — ${pendingToggle.active
            ? 'stops being scanned until it is reactivated.'
            : 'starts being scanned again on the next run.'}`
          : ''}
        confirmLabel={pendingToggle?.active ? 'Deactivate' : 'Reactivate'}
        destructive={!!pendingToggle?.active}
        busy={toggleBusy}
        onConfirm={runToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </Panel>
  );
}

export default function SourceManagerPage() {
  // ── Die Schale: EIN Reiter-Schreiber, EINE einmalige Umschreibung ────────
  // 🔴 Plan §3.2: die Schale haelt NUR `tab`. Der aktive Rumpf haelt seine
  // eigenen Filter — hoechstens ZWEI `useListQuery`-Instanzen gleichzeitig (der
  // inaktive Rumpf ist nicht montiert, sonst schrieben zwei im selben Tick und
  // die zweite faenge die erste ein, `useListQuery.js:234-239`).
  const [searchParams, setSearchParams] = useSearchParams();
  const rohTab = searchParams.get('tab');
  // 🔴 Objektform, NICHT ein Array (Plan §3.2, BL-3): `normalisiereAchse` macht
  // aus einem Array genau dessen Werte — `['sources','topics']` traege also
  // KEIN `'0'`/`'1'`, und `leseAchse` verwuerfe `tab=1` (den ECHTEN Link der
  // Home-Kachel, `HomeDashboard.jsx:196`) samt Meldung „Ignored unknown filter
  // value", bevor die Umschreibung greift.
  const schale = useListQuery({ tabs: { default: 'sources', values: ['sources', 'topics', '0', '1'] } });
  const reiterIndex = schale.state.tab === 'topics' || schale.state.tab === '1' ? 1 : 0;

  // 🔴 Die EINE Migration (`tab=0|1` → Name, unbekannt → Parameter weg). Sie
  // leitet ihr Ziel aus dem ROHEN Wert ab, nicht aus `state.tab`: `leseAchse`
  // bildet einen unbekannten Wert schon auf die Vorgabe ab (`sources`) und der
  // Bereinigungseffekt des Hooks loescht ihn — eine Bedingung an `state.tab`
  // montierte fuer `?tab=foo` den Sources-Rumpf, waehrend dieser Effekt `tab`
  // im selben Tick loescht.
  useEffect(() => {
    if (rohTab == null || rohTab === '' || rohTab === 'sources' || rohTab === 'topics') return;
    setSearchParams((prev) => {
      const naechste = new URLSearchParams(prev);
      if (rohTab === '0') naechste.set('tab', 'sources');
      else if (rohTab === '1') naechste.set('tab', 'topics');
      else naechste.delete('tab');
      return naechste;
    }, { replace: true });
  }, [rohTab, setSearchParams]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSource, setEditSource] = useState(null);
  const [scanningAll, setScanningAll] = useState(false);
  // 🔴 Nach dem Anlegen/Aendern muss der RUMPF neu laden — er haelt die Liste,
  // nicht die Schale. Ein Zaehler statt eines Callbacks: die Schale kennt die
  // Ladefunktion des Rumpfs nicht und soll sie nicht kennen.
  const [quellenVersion, setQuellenVersion] = useState(0);

  // Die Zahlen an der Reiterleiste. Sie gehoeren der SCHALE, weil die Reiter
  // ueber beiden Ruempfen stehen. `null` heisst „unbekannt" und zeichnet keine
  // Pille — nie eine geratene 0 (Regel 22, Komponentenregel 3).
  const [quellenZahl, setQuellenZahl] = useState(null);
  // 🔴 Die Zahl JE PRIORITAET (R16-B1) — der Gruppenkopf der Quellenliste liest
  // sie im ungefilterten Fall. Sie kommt aus DEMSELBEN Abruf wie `total`: zwei
  // Abrufe koennten auseinanderlaufen (dieselbe Begruendung wie in `SourcesBody`).
  // `null` heisst „unbekannt" und faellt dort auf die geladene Liste zurueck —
  // nie 0, nie geraten (Regel 22, Komponentenregel 3).
  const [quellenJePrioritaet, setQuellenJePrioritaet] = useState(null);
  // 🔴 Die Themen-Statistik gehoert der SCHALE, nicht dem Rumpf: die Zahl steht
  // an der Reiterleiste (auch wenn der Quellen-Reiter aktiv ist) UND in der
  // KPI-Reihe UND im Zaehler des Rumpfs. Ein zweiter Abruf im Rumpf koennte
  // auseinanderlaufen — dieselbe Begruendung wie bei `quellenZahl`.
  // `null` = laedt noch; `{}` = Abruf gescheitert (die Kacheln zeigen dann „—",
  // der Reiter traegt keine Pille — nie eine geratene 0).
  const [themenStats, setThemenStats] = useState(null);
  const themenZahl = typeof themenStats?.total === 'number' ? themenStats.total : null;
  // 🔴 R15-Runde 2, N2 (ehrlich benannt, nicht gebaut): die Statistik ist der
  // UNGEFILTERTE Bestand und haengt an keinem Filter — ein Filterwechsel laedt
  // sie darum nicht neu, und eine Aenderung aus einer ANDEREN Sitzung zeigt
  // erst der naechste Refresh (der seit NB1-C1 beide Abrufe faehrt).
  const ladeQuellenZahl = useCallback(() => {
    axios.get(`${API}/api/admin/blog/sources/stats`)
      .then((r) => {
        setQuellenZahl(typeof r.data?.total === 'number' ? r.data.total : null);
        setQuellenJePrioritaet(
          r.data?.by_priority && typeof r.data.by_priority === 'object'
            ? r.data.by_priority
            : null,
        );
      })
      .catch(() => {
        setQuellenZahl(null);
        setQuellenJePrioritaet(null);
      });
  }, []);
  useEffect(() => { ladeQuellenZahl(); }, [ladeQuellenZahl]);
  const ladeThemenStats = useCallback(() => {
    axios.get(`${API}/api/admin/blog/topics/stats`)
      .then((r) => setThemenStats(r.data && typeof r.data === 'object' ? r.data : {}))
      // 🔴 `{}`, nicht `null`: `null` heisst „laedt noch" und liesse die
      // KPI-Kacheln ewig im Geruest stehen. Ein gescheiterter Abruf ist eine
      // Aussage („unbekannt"), kein Ladezustand.
      .catch(() => setThemenStats({}));
  }, []);
  useEffect(() => { ladeThemenStats(); }, [ladeThemenStats]);

  const handleAdd = () => { setEditSource(null); setDialogOpen(true); };
  const handleClose = () => setDialogOpen(false);
  const handleEdit = (source) => { setEditSource(source); setDialogOpen(true); };

  const handleSaved = (saved) => {
    setDialogOpen(false);
    setQuellenVersion((v) => v + 1);
    ladeQuellenZahl();
    showSnack(editSource ? `Saved: ${saved.name}` : `Added: ${saved.name}`);
  };

  const handleScanAll = async () => {
    setScanningAll(true);
    try {
      const r = await axios.post(`${API}/api/admin/blog/scan-all`);
      if (r.data.triggered) {
        showSnack(`Full scan started — scanning ${r.data.scan_enabled_count} source(s). New topics will appear as discovered.`);
      } else if (r.data.reason) {
        showSnack(`N8N_BLOG_SCANNER_WEBHOOK_URL not configured — set this env var on the backend to enable scanning.`, 'warning');
      } else {
        showSnack(r.data.error || 'Scan all failed', 'error');
      }
    } catch (err) {
      showSnack(err.response?.data?.error || 'Scan all failed', 'error');
    } finally {
      setScanningAll(false);
    }
  };

  // 🔴 Die Montagebedingung haengt am ROHEN Parameter (Plan §3.2, B-1): ein
  // Rumpf entsteht NUR, wenn `tab` fehlt/leer ist (dann Vorgabe `sources`) oder
  // ein NAME ist. Bei `'0'`/`'1'`/jedem Unbekannten rendert die Schale ihr
  // Geruest, der Effekt oben schreibt um — erst danach entsteht die
  // Rumpf-Instanz. 🔴 Der Vorgabefall steht IM Ausdruck (B-3): `null` und `''`
  // fallen in den Sources-Zweig, sonst bliebe der Seitenleisten-Einstieg
  // (`/blog-sources` ohne Parameter) dauerhaft auf dem Skelett.
  const rumpf = rohTab === 'topics'
    ? <TopicsInbox stats={themenStats} onZahlGeaendert={ladeThemenStats} />
    : (rohTab == null || rohTab === '' || rohTab === 'sources')
      ? (
        <SourcesBody
          quellenZahl={quellenZahl}
          quellenJePrioritaet={quellenJePrioritaet}
          onZahlGeaendert={ladeQuellenZahl}
          version={quellenVersion}
          onBearbeiten={handleEdit}
        />
      )
      : (
        <Panel pad="none" data-testid="liste-panel">
          <Box sx={{ p: SP.card }}>
            <CardListSkeleton cards={4} lines={3} form="outlined" ariaLabel="Loading sources" />
          </Box>
        </Panel>
      );

  return (
    <PageContainer width="full">
      {/* 🔴 Zonen-Marke `liste-kopf` (§2.2 Punkt 2): sie wird von Hand gestempelt,
          kein Baustein setzt sie. P3 misst die Reihenfolge kopf < reiter < panel. */}
      <Box data-testid="liste-kopf">
        <ViewHeader
          // 🔴 `maxWidth={false}`: die Seite ist `full` und ungedeckelt; ohne
          // diese Zeile deckelte `ViewHeader` auf `CONTENT_WIDTH.reading` und
          // zentrierte sich — der Kopf stuende nicht buendig ueber dem Panel
          // (Hausform `/media`, G11a R16-W5a).
          maxWidth={false}
          title="Blog Source Registry"
          subtitle="Trusted sources injected into blog AI prompts"
          // 🔴 W-4 (R16, 2026-10-08): hier stand `breadcrumbs={[{ label: 'Blog
          // sources' }]}` — EIN Kruemel ohne `href` direkt unter dem eigenen
          // Titel. Ein Brotkruem ist ein RUECKWEG; ein Rueckweg auf dieselbe
          // Seite ist keiner. Regel: `pattern-library.md` §ViewHeader.
          actions={[
            {
              key: 'scan-all',
              label: scanningAll ? 'Scanning…' : 'Scan all',
              variant: 'outlined',
              icon: scanningAll ? <CircularProgress size={14} color="inherit" /> : <ScannerIcon />,
              onClick: handleScanAll,
              disabled: scanningAll,
            },
            {
              key: 'add',
              label: 'Add source',
              variant: 'contained',
              icon: <AddIcon />,
              onClick: handleAdd,
            },
          ]}
        />
      </Box>

      {/* Tabs — W4/F10 der Gates zu PR 4: rohe MUI-`<Tabs>` ohne Panel sind
          laut `pattern-library.md` nur MIT Panel erlaubt; diese Reihe tauscht
          den Inhalt per `value`. Also `UnderlineTabs`.
          🔴 `counts` ist die AUSGERICHTETE Form (P11): beide Reiter tragen eine
          Zahl, auch 0, und die Pille reserviert die Breite der breitesten —
          sonst spraenge die Reihe beim Nachladen. `null` (unbekannt) zeichnet
          keine Pille, nie eine geratene 0. */}
      <Box data-testid="liste-reiter">
        <UnderlineTabs
          tabs={['Sources', 'Topics Inbox']}
          counts={[quellenZahl, themenZahl]}
          value={reiterIndex}
          onChange={(i) => schale.set('tab', i === 0 ? 'sources' : 'topics')}
          sx={{ mb: 3 }}
        />
      </Box>

      {rumpf}

      {/* Edit/Create Dialog — bleibt in der SCHALE: der Kopf traegt „Add source"
          auf BEIDEN Reitern, und es ist derselbe Dialog. */}
      <SourceDialog
        open={dialogOpen}
        source={editSource}
        onClose={handleClose}
        onSaved={handleSaved}
      />

    </PageContainer>
  );
}
